import {
  RedirectToSignIn,
  SignOutButton,
  SignedIn,
  SignedOut,
} from "@clerk/clerk-react";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import type { inferOutput } from "@trpc/tanstack-react-query";
import { OnboardingLayout } from "~/components/app-shell/onboarding-layout";
import { Button } from "~/components/ui/button";
import { RedditTracking, Tracking } from "~/instrumentation/tracking";
import type { trpc } from "~/lib/trpc";
import { type AppRoute, type RouterContext, router } from "~/main";

type OnboardingStateResponse = inferOutput<
  typeof trpc.onboarding.getOnboardingState
>;

export const OnboardingSteps: Array<
  | Exclude<
      OnboardingStateResponse["completedSteps"][number],
      "COMPANY_DESCRIPTION" | "COMPLETE"
    >
  | "POST_ONBOARDING"
> = ["PROVIDE_WEBSITE", "SURVEY", "POST_ONBOARDING"] as const;

type OnboardingStep = (typeof OnboardingSteps)[number];

const OnboardingUrls = {
  PROVIDE_WEBSITE: "/onboarding/provide-website",
  SURVEY: "/onboarding/survey",
  POST_ONBOARDING: "/",
} satisfies Record<OnboardingStep, AppRoute>;

async function getCurrentOnboardingState(context: RouterContext): Promise<
  OnboardingStateResponse & {
    currentStep: OnboardingStep;
  }
> {
  const onboardingState = await context.queryClient.fetchQuery(
    context.trpc.onboarding.getOnboardingState.queryOptions(),
  );

  // Find the first step that hasn't been completed. This allows us to handle changes in order.
  const currentStep = onboardingState.isCompleted
    ? "POST_ONBOARDING"
    : (OnboardingSteps.find(
        (step) =>
          step !== "POST_ONBOARDING" &&
          !onboardingState.completedSteps.includes(step),
      ) ?? "POST_ONBOARDING");

  console.log("Derived step: ", currentStep);

  return {
    ...onboardingState,
    currentStep,
  };
}

export type OnboardingContext = OnboardingStateResponse & {
  currentStep: OnboardingStep;
  next: () => Promise<void>;
};

export const Route = createFileRoute("/onboarding")({
  component: RouteComponent,
  head: () => ({
    meta: [{ title: "Onboarding" }],
  }),
  validateSearch: (search: Record<string, unknown>): { source?: "sign-up" } => {
    if (search.source === "sign-up") {
      return { source: "sign-up" };
    }
    return {};
  },
  beforeLoad: async ({ context, location, search }) => {
    if (search.source === "sign-up") {
      Tracking.capture({
        event: "SIGN_UP",
        data: { email: context.user?.email },
      });
      RedditTracking.track({
        event: "SignUp",
        conversionId: context.auth.userId ?? undefined,
      });
    }

    const onboardingState = await getCurrentOnboardingState(context);

    const { currentStep } = onboardingState;

    if (OnboardingUrls[currentStep] !== location.pathname) {
      throw redirect({
        to: OnboardingUrls[currentStep],
        mask:
          currentStep !== "POST_ONBOARDING" ? { to: "/onboarding" } : undefined,
        replace: true,
      });
    }

    return {
      ...context,
      onboardingState: {
        ...onboardingState,
        next: async () => {
          await router.invalidate({ sync: true });
        },
      } satisfies OnboardingContext,
    };
  },
});

function RouteComponent() {
  const { onboardingState } = Route.useRouteContext();
  if (!onboardingState) {
    return null;
  }
  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <OnboardingLayout
          onboardingState={onboardingState}
          button={
            <SignOutButton>
              <Button size="sm" variant="outline" className="bg-white">
                Sign out
              </Button>
            </SignOutButton>
          }
        >
          <Outlet />
        </OnboardingLayout>
      </SignedIn>
    </>
  );
}
